import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes/routes';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import type { ReservationHolder } from '@/infrastructure/FulfillmentAdminClient';
import { groupHolders, holderUnits } from '../inventoryView';

export interface ReservedHoldersModalProps {
  sku: string;
  holders: ReservationHolder[];
  loading: boolean;
  /** The read failed, which says nothing at all about who holds the sku. */
  failed: boolean;
  onRetry: () => void;
  onClose: () => void;
}

const HolderRows = ({ holders }: { holders: ReservationHolder[] }) => (
  <>
    {holders.map((holder) => (
      <tr key={`${holder.orderId}-${holder.lineNo}-${holder.locationCode ?? ''}`}>
        <td>
          <Link to={ROUTES.fulfillment.orderDetail(holder.orderId)}>
            #{holder.orderId} / line {holder.lineNo}
          </Link>
        </td>
        <td>
          <span className="loc-pill">{holder.locationCode || 'Unassigned'}</span>
        </td>
        <td className="num">{holder.qty}</td>
      </tr>
    ))}
  </>
);

/**
 * Who holds a sku's stock right now, read-only: releasing stays on the Current Stocks row, where the
 * shelf is known. Holders pinned to no shelf are listed apart, because the Reserved card never counts them.
 */
export const ReservedHoldersModal = ({
  sku,
  holders,
  loading,
  failed,
  onRetry,
  onClose,
}: ReservedHoldersModalProps) => {
  const dialog = useModalDialog(onClose);
  const { onShelf, unassigned } = groupHolders(holders);

  return (
    <dialog
      className="inv-modal"
      ref={dialog}
      aria-label={`Reserving orders ${sku}`}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <header>
        Reserving Orders — {sku}
        <button type="button" className="x" aria-label="Close reserving orders" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        <div className="table-wrap capped">
          <table className="tbl">
            <thead>
              <tr>
                <th>Order / line</th>
                <th>Location</th>
                <th>Reserved Qty</th>
              </tr>
            </thead>
            <tbody>
              <HolderRows holders={onShelf} />
              {unassigned.length > 0 && (
                <>
                  <tr>
                    <td colSpan={3} className="blocked-note">
                      Not on a shelf — {holderUnits(unassigned)} units, not counted in Reserved Qty
                    </td>
                  </tr>
                  <HolderRows holders={unassigned} />
                </>
              )}
              {/* A failed read must never wear the empty state: "nobody holds this" is a finding, not a silence. */}
              {holders.length === 0 && (
                <tr>
                  <td colSpan={3} className="blocked-note">
                    {loading ? (
                      'Loading the reserving orders…'
                    ) : failed ? (
                      <>
                        The reserving orders could not be read.{' '}
                        <button
                          type="button"
                          className="btn btn-line btn-sm"
                          aria-label="Retry reading the reserving orders"
                          onClick={onRetry}
                        >
                          Retry
                        </button>
                      </>
                    ) : (
                      'No order is reserving this sku.'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {holders.length > 0 && (
          <div className="note">
            {onShelf.length} order lines reserve {holderUnits(onShelf)} units on shelves. Releasing a reservation is
            done from the Reserved figure on the Current Stocks row.
          </div>
        )}
      </div>
      <div className="foot">
        <button type="button" className="btn btn-gray btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </dialog>
  );
};
